'use client';

import { useState, useEffect } from 'react';
import { IconShare, IconBuilding, IconDocument } from '@repo/svgs';
import type { Building } from '@repo/api';
import { Tab } from '@/components/ui/Tab';
import { Badge } from '@/components/ui/Badge';
import { useStore } from '@/providers/StoreProvider';
import { useBuilding } from '../../context/BuildingProvider';
import { cn } from '@/lib/cn';
import { DynamicGridContainer } from '@/components/ui';
import { usePathname, useSearchParams } from 'next/navigation';
import { BuildingBreadcrumbs } from './BuildingBreadcrumbs';
import { ShareButton } from '@/components/share/ShareButton';
import { useAnalytics } from '@/hooks/useAnalytics';

interface BuildingHeaderProps {
  building: Building;
  city?: string;
}

export function BuildingHeader({ building, city }: BuildingHeaderProps) {
  const { activeTab, setActiveTab } = useBuilding();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const analytics = useAnalytics();
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const explorer = useStore(s => s.explorer.data);
  const showReportBadge = !isLoggedIn || !explorer?.accessType;
  const [currentUrl, setCurrentUrl] = useState<string>('');

  // Set current URL in useEffect to avoid SSR issues
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, [pathname, searchParams]);

  const handleTabChange = (tab: 'overview' | 'report') => {
    setActiveTab(tab);
    // Update URL with query parameter
    const params = new URLSearchParams(searchParams.toString());
    if (tab === 'report') {
      void analytics.track('building_reportview');
      params.set('buildingReport', 'true');
    } else {
      void analytics.track('building_profileview');
      params.delete('buildingReport');
    }
    const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', newUrl);
      setCurrentUrl(window.location.href);
    }
  };

  return (
    <div className=" bg-primary" id="building-header">
      {/* Breadcrumbs and Actions */}
      <div className="flex items-center justify-between max-w-[1280px] gap-2 px-6 md:px-16 pt-3 pb-2 w-full mx-auto">
        {/* Breadcrumbs. No clipping on mobile — the strip bleeds past this
            container's padding to scroll edge-to-edge. */}
        <div className="min-w-0 flex-1 md:overflow-hidden">
          <BuildingBreadcrumbs building={building} />
        </div>

        {/* Action Container */}
        <div className="hidden md:flex gap-2  items-center justify-end shrink-0">
          <ShareButton
            className={cn(
              'cursor-pointer flex items-center justify-center',
              'p-3 rounded-full',
              'hover:bg-secondary transition-colors',
            )}
            aria-label="Share"
            title={building?.name ?? building?.primaryAddress?.streetAddress ?? 'Building'}
            url={currentUrl}
            icon={<IconShare className="w-4 h-4 icon-primary" />}
          />
        </div>
      </div>

      {/* Tabs */}
      {city === 'nyc' && (
        <div className="bg-primary">
          <div className="relative z-10 shadow-md bg-primary">
            <DynamicGridContainer className="relative ">
              {/* Mobile: the grid container already adds 16px per side, so px-2 lands
                  the tabs 24px from the edges; they split the row 50-50 with no gap. */}
              <div className="flex items-center px-2 gap-0 w-full md:px-4 md:gap-4">
                <Tab
                  background="primary"
                  active={activeTab === 'overview'}
                  onClick={() => handleTabChange('overview')}
                  icon={<IconBuilding />}
                  className="min-w-0 flex-1 md:min-w-fit md:flex-none"
                >
                  Overview
                </Tab>
                <Tab
                  background="primary"
                  active={activeTab === 'report'}
                  onClick={() => handleTabChange('report')}
                  icon={<IconDocument />}
                  badge={showReportBadge ? <Badge type="alert" /> : undefined}
                  className="min-w-0 flex-1 md:min-w-fit md:flex-none"
                >
                  Report
                </Tab>
              </div>
            </DynamicGridContainer>
          </div>
        </div>
      )}
    </div>
  );
}
